import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { Plus, X, AlertTriangle, PackagePlus, PackageMinus, Trash2, Pencil, Package, Wrench } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";
import { fmtCurrency } from "@/lib/constants";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";
import Equipment from "@/pages/Equipment";

export default function Inventory() {
  const [tab, setTab] = useState("parts");
  const [parts, setParts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/parts${onlyLow ? "?low_stock=true" : ""}`);
      setParts(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
   
  useEffect(() => { load(); }, [onlyLow]);

  const adjust = async (id, delta) => {
    try { await api.post(`/parts/${id}/adjust`, { delta, reason: "manual" }); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const ids = parts.map((p) => p.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Delete ${idsArr.length} part(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/parts/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 inline-flex rounded-md border border-border/70 bg-secondary/40 p-1">
        <button
          data-testid="inventory-tab-parts"
          onClick={() => setTab("parts")}
          className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${tab === "parts" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Package className="h-3.5 w-3.5" /> Parts & Spares
        </button>
        <button
          data-testid="inventory-tab-equipment"
          onClick={() => setTab("equipment")}
          className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${tab === "equipment" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Wrench className="h-3.5 w-3.5" /> Equipment
        </button>
      </div>

      {tab === "equipment" ? <Equipment /> : (
      <>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <p className="label-eyebrow text-primary">Warehouse</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Inventory</h1>
          <p className="mt-1 text-sm text-muted-foreground">Parts, spares and consumables with low-stock alerts.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs">
            <input data-testid="parts-low-stock-filter" type="checkbox" checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} />
            Show only low stock
          </label>
          <button data-testid="parts-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New part
          </button>
          <ExportButton resource="parts" testid="parts-export" />
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="parts-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Part</th>
              <th className="px-4 py-2 label-eyebrow">SKU</th>
              <th className="px-4 py-2 label-eyebrow">Category</th>
              <th className="px-4 py-2 label-eyebrow">Price</th>
              <th className="px-4 py-2 label-eyebrow">Stock</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => {
              const low = (p.stock ?? 0) <= (p.reorder_level ?? 0);
              return (
                <tr key={p.id} data-testid={`part-row-${p.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <RowCheckbox testid={`part-select-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} />
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-medium">{p.name}</p>
                    <p className="text-[11px] text-muted-foreground">{p.warehouse || "—"}</p>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{p.sku || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{p.category || "—"}</td>
                  <td className="px-4 py-2">{fmtCurrency(p.unit_price)}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-semibold ${low ? "text-orange-400" : ""}`}>{p.stock}</span>
                      {low && <AlertTriangle className="h-3.5 w-3.5 text-orange-400" />}
                      <span className="text-[10px] text-muted-foreground">/ reorder {p.reorder_level}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`part-edit-${p.id}`} onClick={() => setEditing(p)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`part-inc-${p.id}`} onClick={() => adjust(p.id, +1)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-emerald-500"><PackagePlus className="h-3.5 w-3.5" /></button>
                      <button data-testid={`part-dec-${p.id}`} onClick={() => adjust(p.id, -1)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-red-500"><PackageMinus className="h-3.5 w-3.5" /></button>
                      <button
                        data-testid={`part-delete-${p.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete part "${p.name}"?`)) return;
                          try { await api.delete(`/parts/${p.id}`); await load(); }
                          catch (e) { setErr(formatApiError(e)); }
                        }}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {parts.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-muted-foreground">No parts.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="parts" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <PartModal existingParts={parts} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <PartModal existingParts={parts} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </>
      )}
    </div>
  );
}

function PartModal({ onClose, onSaved, existing, existingParts = [] }) {
  const [form, setForm] = useState({
    name: existing?.name || "", sku: existing?.sku || "", category: existing?.category || "",
    unit_price: existing?.unit_price ?? 0, stock: existing?.stock ?? 0,
    reorder_level: existing?.reorder_level ?? 5, warehouse: existing?.warehouse || "",
    description: existing?.description || "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, unit_price: Number(form.unit_price), stock: Number(form.stock), reorder_level: Number(form.reorder_level) };
      if (existing) await api.patch(`/parts/${existing.id}`, payload);
      else await api.post("/parts", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? `Edit part — ${existing.name}` : "New part"}</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          {[
            ["name", "Name *", true, true, "text", "part-names"],
            ["sku", "SKU", false, false, "text", "part-skus"],
            ["category", "Category", false, false, "text", "part-categories"],
            ["unit_price", "Unit price", false, false, "number"],
            ["stock", "Opening stock", false, false, "number"],
            ["reorder_level", "Reorder level", false, false, "number"],
            ["warehouse", "Warehouse", false, false, "text", "part-warehouses"],
          ].map(([k, l, req, full, type, list]) => (
            <div key={k} className={full ? "sm:col-span-2" : ""}>
              <label className="label-eyebrow mb-1.5 block">{l}</label>
              <input required={!!req} type={type || "text"} list={list} value={form[k]} onChange={set(k)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ))}
          {/* Autocomplete suggestions sourced from existing parts */}
          <datalist id="part-names">
            {[...new Set(existingParts.map((p) => p.name).filter(Boolean))].map((v) => <option key={v} value={v} />)}
          </datalist>
          <datalist id="part-skus">
            {[...new Set(existingParts.map((p) => p.sku).filter(Boolean))].map((v) => <option key={v} value={v} />)}
          </datalist>
          <datalist id="part-categories">
            {[...new Set(existingParts.map((p) => p.category).filter(Boolean))].map((v) => <option key={v} value={v} />)}
          </datalist>
          <datalist id="part-warehouses">
            {[...new Set(existingParts.map((p) => p.warehouse).filter(Boolean))].map((v) => <option key={v} value={v} />)}
          </datalist>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create part")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
